import { BrowserRouter, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import ClinicalFolder from "./pages/ClinicalFolder";
import ClinicalDocEditorPage from "./pages/ClinicalDocEditorPage";
import InviteLanding from "./pages/InviteLanding";
import { useEffect, useRef } from "react";
import "./App.css";
import "./i18n";
import { Toaster } from "sonner";
import Home from "./pages/Home";
import Login from "./pages/Login";
import RegisterUser from "./pages/RegisterUser";
import RegisterPro from "./pages/RegisterPro";
import RegisterOrg from "./pages/RegisterOrg";
import OrganizationDetail from "./pages/OrganizationDetail";
import Organizations from "./pages/Organizations";
import ConfirmEmail from "./pages/ConfirmEmail";
import Feed from "./pages/Feed";
import Professionals from "./pages/Professionals";
import ProfessionalDetail from "./pages/ProfessionalDetail";
import HealthBio from "./pages/HealthBio";
import OrgBio from "./pages/OrgBio";
import FollowersList from "./pages/FollowersList";
import Drive from "./pages/Drive";
import PamConnect from "./pages/PamConnect";
import PamBill from "./pages/PamBill";
import PamcEmbed from "./pages/PamcEmbed";
import Chat from "./pages/Chat";
import Events from "./pages/Events";
import EventDetail from "./pages/EventDetail";
import ServiceDetail from "./pages/ServiceDetail";
import ProductDetail from "./pages/ProductDetail";
import CreateEvent from "./pages/CreateEvent";
import MyEvents from "./pages/MyEvents";
import Agenda from "./pages/Agenda";
import Bookings from "./pages/Bookings";
import OrgServices from "./pages/OrgServices";
import OrgShop from "./pages/OrgShop";
import Orders from "./pages/Orders";
import VideoRoom from "./pages/VideoRoom";
import Admin from "./pages/Admin";
import AdminPamConnect from "./pages/AdminPamConnect";
import AdminPro from "./pages/AdminPro";
import Settings from "./pages/Settings";
import FrozenScreen from "./pages/FrozenScreen";
import SearchResults from "./pages/SearchResults";
import Notifications from "./pages/Notifications";
import Followers from "./pages/Followers";
import Legal from "./pages/Legal";
import LegalConsent from "./pages/LegalConsent";
import PaymentResult from "./pages/PaymentResult";
import PublicProfile from "./pages/PublicProfile";
import StaffEvent from "./pages/StaffEvent";
import { InstallPrompt } from "./components/InstallPrompt";
import { RegisterChoiceGate } from "./components/RegisterChoice";
import { FAVICON_URL, api, getUser } from "./api";
import OrientationGuard from "./components/OrientationGuard";

// Rientro automatico all'avvio: controllo server-side una sola volta, senza polling. Evita loop se gia' nella stanza.
function AutoReentry() {
  const nav = useNavigate();
  const loc = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return; done.current = true;
    if (!getUser()) return;
    if (loc.pathname.startsWith("/video/") || loc.pathname === "/login") return;
    api.get("/video/active").then(({ data }) => { if (data.active) nav(`/video/${data.active.id}?resume=1`); }).catch(() => {});
  }, []); // eslint-disable-line
  return null;
}

// Gating legale: redirect a /legal/consent quando l'utente ha accettazioni pendenti.
// Ordine: sicurezza (login) -> video attivo (AutoReentry) -> legal pending -> destinazione.
function LegalGate() {
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!getUser()) return;
    const p = loc.pathname;
    if (p.startsWith("/video/") || p.startsWith("/legal/consent") || p === "/login") return;
    api.get("/legal/pending").then(({ data }) => {
      if (data.pending && data.pending.length) {
        const next = encodeURIComponent(p + loc.search);
        nav(`/legal/consent?next=${next}`, { replace: true });
      }
    }).catch(() => {});
  }, [loc.pathname]); // eslint-disable-line
  return null;
}

// Congelamento account: reindirizza gli account paused/deactivation_requested alla schermata stato.
function AccountGate() {
  const nav = useNavigate();
  const loc = useLocation();
  useEffect(() => {
    if (!getUser()) return;
    const p = loc.pathname;
    if (p.startsWith("/account-status") || p === "/login" || p.startsWith("/legal") || p.startsWith("/video/")) return;
    api.get("/auth/me").then(({ data }) => {
      if (["paused", "deactivation_requested"].includes(data.account_status)) {
        nav("/account-status", { replace: true });
      }
    }).catch(() => {});
  }, [loc.pathname]); // eslint-disable-line
  return null;
}

function App() {
  useEffect(() => {
    let l = document.querySelector("link[rel~='icon']");
    if (!l) { l = document.createElement("link"); l.rel = "icon"; document.head.appendChild(l); }
    l.href = FAVICON_URL;
    document.title = "YouPAM";
  }, []);
  return (
    <BrowserRouter>
      <Toaster position="top-right" richColors />
      <InstallPrompt />
      <RegisterChoiceGate />
      <OrientationGuard />
      <AutoReentry />
      <LegalGate />
      <AccountGate />
      <Routes>
        <Route path="/" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register/user" element={<RegisterUser />} />
        <Route path="/register/pro" element={<RegisterPro />} />
        <Route path="/register/org" element={<RegisterOrg />} />
        <Route path="/organizations" element={<Organizations />} />
        <Route path="/organizations/:id" element={<OrganizationDetail />} />
        <Route path="/organizations/:id/followers" element={<FollowersList kind="org" />} />
        <Route path="/professionals/:id/followers" element={<FollowersList kind="pro" />} />
        <Route path="/confirm-email" element={<ConfirmEmail />} />
        <Route path="/feed" element={<Feed />} />
        <Route path="/professionals" element={<Professionals />} />
        <Route path="/professionals/:id" element={<ProfessionalDetail />} />
        <Route path="/p/:username" element={<PublicProfile />} />
        <Route path="/bio" element={<HealthBio />} />
        <Route path="/bio/:patientId" element={<HealthBio />} />
        <Route path="/org-bio/:patientId" element={<OrgBio />} />
        <Route path="/drive" element={<Drive />} />
        <Route path="/pam-connect" element={<PamConnect />} />
        <Route path="/pam-bill" element={<PamBill />} />
        <Route path="/pamc-embed" element={<PamcEmbed />} />
        <Route path="/clinica/:patientId" element={<ClinicalFolder />} />
        <Route path="/redigi-documento" element={<ClinicalDocEditorPage />} />
        <Route path="/invite/:token" element={<InviteLanding />} />
        <Route path="/chat" element={<Chat />} />
        <Route path="/chat/:otherId" element={<Chat />} />
        <Route path="/events" element={<Events />} />
        <Route path="/events/:id" element={<EventDetail />} />
        <Route path="/services/:id" element={<ServiceDetail />} />
        <Route path="/products/:id" element={<ProductDetail />} />        <Route path="/events/create" element={<CreateEvent />} />
        <Route path="/events/:id/edit" element={<CreateEvent />} />
        <Route path="/my-events" element={<MyEvents />} />
        <Route path="/agenda" element={<Agenda />} />
        <Route path="/admin-pro" element={<AdminPro />} />
        <Route path="/admin-org" element={<AdminPro />} />
        <Route path="/bookings" element={<Bookings />} />
        <Route path="/services" element={<OrgServices />} />
        <Route path="/shop" element={<OrgShop />} />
        <Route path="/orders" element={<Orders />} />
        <Route path="/video/:bid" element={<VideoRoom />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/pam-connect" element={<AdminPamConnect />} />
        <Route path="/settings" element={<Settings />} />
        <Route path="/account-status" element={<FrozenScreen />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/followers" element={<Followers />} />
        <Route path="/legal/consent" element={<LegalConsent />} />
        <Route path="/legal/:doc" element={<Legal />} />
        <Route path="/payment/success" element={<PaymentResult ok />} />
        <Route path="/payment/cancel" element={<PaymentResult ok={false} />} />
        <Route path="/event-staff/:token" element={<StaffEvent />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </BrowserRouter>
  );
}
export default App;
